const RUNTIME_FILE_RE = /\.(?:png|jpe?g|gif|webp|svg|mp3|wav|m4a|aac|ogg|mp4|webm|mov|m4v|pdf)$/i;

export const REQUIRED_EXACT_RUNTIME_KEYS = [
  'Images/TemplateGraphics/e-Book/arrow_left.png',
  'Images/TemplateGraphics/e-Book/arrow_right.png',
  'Images/Images/Login/background1.jpg',
  'Images/Images/Login/background2.jpg',
  'Images/Images/Login/background3.jpg',
  'Images/Images/Login/background4.jpg',
  'Images/SuccessAnimations/1.gif',
  'Images/SuccessAnimations/2.gif',
  'Images/SuccessAnimations/3.gif',
  'Images/SuccessAnimations/4.gif',
  'Images/SuccessAnimations/5.gif',
  'Images/SuccessAnimations/6.gif',
  'Images/SuccessAnimations/7.gif',
  'Audio/Generic/ActivityResults/English/applause1.mp3',
  'Audio/Generic/ActivityResults/English/applause2.mp3',
  'Audio/Generic/ActivityResults/Spanish/applause1.mp3',
  'Audio/Generic/ActivityResults/Spanish/applause2.mp3',
  // Core Help/runtime controls are direct image URLs, not data-driven refs.
  // Keep them explicit so exact-content offline packs never drop them when
  // includeShared=false is used to avoid downloading the entire shared tree.
  'Images/Images/Help/Close50.png',
  'Images/Images/Help/HelpButton.png',
  'Images/Images/Audio/audio_icon1.png',
  'Images/Images/Audio/audiostop.png',
  'Images/Images/Audio/audio_play.png',
  'Images/Images/Audio/audio_replay.png',
  // Mobile Custom Level renders these directly from MainMenuBoard/FlinkPlayIcons.
  // They are shell assets, not Placement-only assets.
  'Files/LandingPageAssets/Logo/Family_Logo_2024.svg',
  'Images/FlinkPlay/PlayAgain150.png',
  'Images/FlinkPlay/menu.png',
  'Images/FlinkPlay/stars/1-1.png',
  'Images/FlinkPlay/stars/1-2.png',
  'Images/FlinkPlay/stars/1-3.png',
  'Images/Images/Timer/timer_stop.png',
];

export const OFFLINE_MANIFEST_ALLOWED_EXACT_PREFIXES = [
  'Images/',
  'Audio/',
  'Files/',
  'Themes/',
  'public/Activities/',
  'public/Users/',
];

export const MOBILE_ASSET_AUDIT_RUNTIME_PREFIXES = [
  'Images/GlobalButtons/',
  'Images/Images/Login/',
  'Images/TemplateGraphics/',
  'Images/Images/Audio/',
  'Images/Images/Help/',
  'Images/SuccessAnimations/',
  'Themes/KidsFantasy/',
  'Audio/Generic/ActivityResults/English/',
  'Audio/Generic/ActivityResults/Spanish/',
  'Images/Audio/',
  'Images/Help/',
  'Themes/KidsPatterns/',
  'Audio/Generic/Feedback/',
  'Files/animations/ActivityResults/',
  'Files/animations/ParentVideos/',
  'Files/LandingPageAssets/Logo/',
  'Images/FlinkPlay/',
  'Images/ReportMenuImages/',
];

export const PLACEMENT_REQUIRED_SHARED_KEYS = [
  'Images/FlinkPlay/PlayAgain150.png',
  'Images/FlinkPlay/menu.png',
  'Images/FlinkPlay/stars/1-2.png',
  'Images/FlinkPlay/stars/1-3.png',
  'Images/Images/Timer/timer_stop.png',
  // Keep the long-standing Placement baseline as a fallback. The dynamic theme
  // discovery below adds the complete assigned theme backgrounds on top of it.
  'Themes/KidsFantasy/Backgrounds/1.jpg',
  'Themes/KidsFantasy/Backgrounds/2.jpg',
  'Themes/KidsFantasy/Backgrounds/3.jpg',
  'Themes/KidsFantasy/Backgrounds/4.jpg',
  'Themes/KidsFantasy/Backgrounds/5.jpg',
  'Themes/KidsFantasy/Backgrounds/6.jpg',
  'Themes/KidsFantasy/Backgrounds/10.jpg',
];

const idOf = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const cleanRelative = (value = '') => {
  const raw = String(value || '').split('#')[0].split('?')[0].trim();
  if (!raw || /^data:|^blob:/i.test(raw)) return '';
  let decoded = raw;
  try { decoded = decodeURIComponent(raw); } catch (_) {}
  return decoded.replace(/\\/g, '/').replace(/^\.\/+/, '').replace(/^\/+/, '');
};

const normalizeActivityRoot = (activityId, contentFolder) => {
  const folder = cleanRelative(contentFolder);
  if (/^public\/Activities\/[A-Za-z0-9_-]{1,128}(?:\/|$)/.test(folder)) {
    return folder.replace(/\/+$/, '');
  }
  return activityId ? `public/Activities/${activityId}` : '';
};

export function collectActivityRuntimeAssetKeys(
  value,
  {
    activityId = '',
    contentFolder = '',
    canonicalize,
    keys = new Set(),
    depth = 0,
  } = {},
) {
  if (depth > 16 || value === null || value === undefined) return keys;

  if (typeof value === 'string') {
    const exact = typeof canonicalize === 'function' ? canonicalize(value) : '';
    if (exact && !exact.endsWith('/data.json') && !exact.endsWith('/gameData.json')) {
      keys.add(exact);
      return keys;
    }

    const relative = cleanRelative(value);
    if (!relative || !RUNTIME_FILE_RE.test(relative) || relative.includes('..')) return keys;

    const root = normalizeActivityRoot(String(activityId || ''), contentFolder);
    if (!root) return keys;

    // Only explicit activity-relative paths belong in the runtime validation
    // contract. Bare filenames inside legacy Activity JSON are not reliable
    // source references: many are stale, optional, language-specific, or were
    // intentionally deleted. The activity base/language TARs already contain
    // every source object that actually exists, so guessing
    // <activity>/gamedata/<filename> here creates false hard dependencies.
    if (relative.startsWith('gamedata/') || relative.startsWith('languageVersionData/')) {
      keys.add(`${root}/${relative}`);
    }
    return keys;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectActivityRuntimeAssetKeys(item, {
      activityId,
      contentFolder,
      canonicalize,
      keys,
      depth: depth + 1,
    }));
    return keys;
  }

  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectActivityRuntimeAssetKeys(item, {
      activityId,
      contentFolder,
      canonicalize,
      keys,
      depth: depth + 1,
    }));
  }

  return keys;
}

const themeById = (solution, themeId) => {
  const id = String(themeId || '');
  if (!id) return null;
  return (Array.isArray(solution?.themes) ? solution.themes : [])
    .find((theme) => idOf(theme) === id) || null;
};

const toThemeDescriptor = (solution, value) => {
  const id = idOf(value);
  const objectValue = value && typeof value === 'object' ? value : null;
  const resolved = objectValue?.name ? objectValue : themeById(solution, id);
  const name = String(resolved?.name || '').trim();
  if (!id || !name) return null;
  return { id, name };
};

export function getRuntimeThemeDescriptors(playData, scope = 'custom-level') {
  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const solution = (playData?.product?.solutions || [])
    .find((item) => String(item?._id || '') === solutionId)
    || (playData?.product?.solutions || [])[0]
    || null;
  if (!solution) return [];

  const defaultTheme = toThemeDescriptor(solution, solution.defaultTheme);
  if (scope === 'placement') return defaultTheme ? [defaultTheme] : [];

  // Settings exposes every theme assigned to the active Solution. Keep those
  // assigned theme backgrounds offline as one coherent Settings contract; do not
  // make a selectable theme resolve to a white screen after Save.
  const configuredValue = playData?.learner?.settings?.bySolution?.[solutionId]?.theme;
  const configuredTheme = toThemeDescriptor(solution, configuredValue);
  const assignedThemes = (Array.isArray(solution?.themes) ? solution.themes : [])
    .map((theme) => toThemeDescriptor(solution, theme))
    .filter(Boolean);

  const all = [configuredTheme, defaultTheme, ...assignedThemes].filter(Boolean);
  return [...new Map(all.map((theme) => [theme.id, theme])).values()];
}
